import { useState } from 'react';

import { useFieldArray, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import InputAutocomplete from 'shared/components/Input/InputAutocomplete';
import { InputAutocompleteOption } from 'shared/components/Input/InputAutocomplete/InputAutocomplete';
import InputForm from 'shared/components/Input/InputForm';
import Loader from 'shared/containers/Loader';
import { APIErrorT } from 'shared/models/api';
import SCHCTemplate from 'shared/models/SCHCTemplate';
import api from 'utils/api';
import { generateRandomString } from 'utils/string';

import { FormValues } from '../DeviceProfileManageData';

interface TemplateParametersArrayItemT extends SCHCTemplate.API.ParametersT {
  id: string;
}

function ManageTemplateParameters() {
  const {
    setValue,
    register,
    getValues,
  } = useFormContext<FormValues>();

  const defaultV = getValues();
  const { deviceProfileId } = useParams() as any;
  const isEdit = deviceProfileId !== undefined;

  const {
    fields, replace,
  } = useFieldArray({ name: 'templateParameters' });

  const [isLoadingTemplateParameters, setIsLoadingTemplateParameters] = useState(false);

  const onSelectDeviceTemplate = (value: any) => {
    setValue('templateId', value?.id);
    if (value?.id) {
      // Item selected
      setIsLoadingTemplateParameters(true);
      api.SCHCTemplates.getOne(value.id)
        .then((resp: any) => {
          if (resp.status >= 400) {
            resp.json()
              .then((error: APIErrorT) => {
                toast.error(error?.message);
              });
            setIsLoadingTemplateParameters(false);
          } else {
            resp.json()
              .then((result: SCHCTemplate.API.BodyT) => {
                replace(result.parameters);
                setIsLoadingTemplateParameters(false);
              });
          }
        });
    } else {
      // Item cleared
      replace([]);
    }
  };

  const renderInputText = (id: string | undefined, name: string) => (
    `ID: ${id} - ${name}`
  );

  const formatFieldName = (index:number) => (
    `Index #${index} - ${(fields[index] as TemplateParametersArrayItemT).placeHolder}`
  );

  const disabledParameterRegex = /\$[0-9A-Za-z]*\(\)/; // Start with $ and end with ()
  const parameterIsDisabled = (index: number) => disabledParameterRegex.test(
    (fields[index] as TemplateParametersArrayItemT).placeHolder,
  );

  return (
    <>
      <InputAutocomplete
        autocompleteName="device template"
        optionFormatFn={(rawOption: SCHCTemplate.API.BodyT): InputAutocompleteOption => ({
          label: `ID: ${rawOption.sCHCTemplateID} - ${rawOption.name}`,
          groupBy: '',
          id: rawOption.iD,
        })}
        apiCall={api.SCHCTemplates}
        required
        onChangeFn={onSelectDeviceTemplate}
        defaultValue={
            isEdit
              ? renderInputText(defaultV.sCHCTemplateID, defaultV.templateName)
              : undefined
        }
      />

      <div className="form__section">
        <h3>Template Parameters</h3>
        <div className="fields-side-line">
          <div>
            {
                fields.map((_, index) => (
                  <InputForm.TextField
                    fullWidth
                    sx={{ m: 1 }}
                    key={generateRandomString()}
                    label={formatFieldName(index)}
                    defaultValue={(fields[index] as TemplateParametersArrayItemT).profileValue}
                    register={register(`templateParameters.${index}.profileValue`)}
                    required
                    disabled={parameterIsDisabled(index)}
                  />
                ))
            }
          </div>
        </div>
      </div>

      {
        isLoadingTemplateParameters
          ? <div style={{ height: '10rem' }}><Loader.Section disableShrink /></div>
          : null
      }
    </>
  );
}

export default ManageTemplateParameters;
